/* eslint-disable react/prop-types */
import React from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';
import { ThemeProvider } from 'styled-components';
import { withPrismicPreview } from 'gatsby-plugin-prismic-previews';

import { linkResolver } from '../utils/linkResolver';
import Layout from '../components/Layout';
import theme from '../ui/theme';
import Grid from '../components/Grid';
import Cell from '../components/Cell';
import Block from '../components/Block';
import H2 from '../components/H2';
import P from '../components/P';
import EventsList from '../components/EventsList';
import RichTextHtml from '../components/RichTextHtml';
import SEO from '../components/SEO';
import Image from '../components/Image';
import ContainerHeader from '../components/ContainerHeader';

const EventTemplate = ({ data: { prismicEvent }, location, pageContext }) => (
  <ThemeProvider theme={theme}>
    <Layout
      selectedLocale={pageContext.lang}
      location={location}
      hasHero={false}
    >
      <SEO
        selectedLocale={pageContext.lang}
        location={location}
        specifics={{
          title: prismicEvent.data.meta_title || prismicEvent.data.title.text,
          description: prismicEvent.data.meta_description,
          image: prismicEvent.data.share_image.url,
        }}
      />

      <ContainerHeader
        py={{ mobile: 'baseUp6', smallTablet: 'baseUp8', tablet: 'baseUp10' }}
        backgroundColor="grayLight"
      />
      <Grid>
        <Cell size={{ mobile: 1, smallTablet: 1 / 2 }}>
          <Block
            px="baseUnit"
            py={{
              mobile: 'baseUnit',
              smallTablet: 'baseUp12',
              tablet: 'baseUp20',
            }}
            pt={{ mobile: 'baseUp8' }}
          >
            <Image content={prismicEvent.data.image} />
          </Block>
        </Cell>
        <Cell size={{ mobile: 1, smallTablet: 1 / 2 }}>
          <Block
            px="baseUnit"
            py={{
              mobile: 'baseUnit',
              smallTablet: 'baseUp12',
              tablet: 'baseUp20',
            }}
            pb={{ mobile: 'baseUp8' }}
          >
            <H2 color="coral">{prismicEvent.data.title.text}</H2>
            <P mb={{ mobile: 'baseUp4', smallTablet: 'baseUp8' }}>
              {prismicEvent.data.date}
            </P>
            <RichTextHtml html={prismicEvent.data.description.html} />
          </Block>
        </Cell>
      </Grid>

      <Block
        pb={{
          mobile: 'baseUnit',
          smallTablet: 'baseUp12',
          tablet: 'baseUp20',
        }}
      >
        <EventsList
          selectedLocale={pageContext.lang}
          excludeUid={prismicEvent.uid}
        />
      </Block>
    </Layout>
  </ThemeProvider>
);

EventTemplate.propTypes = {
  data: PropTypes.shape({
    prismicEvent: PropTypes.shape({
      uid: PropTypes.string,
      data: PropTypes.shape({
        meta_title: PropTypes.string,
        meta_description: PropTypes.string,
        share_image: PropTypes.shape({
          url: PropTypes.string,
        }),
        date: PropTypes.string,
        title: PropTypes.shape({
          text: PropTypes.string,
        }),
        description: PropTypes.shape({
          html: PropTypes.string,
        }),
        body: PropTypes.arrayOf(PropTypes.shape({})),
        image: PropTypes.shape({
          url: PropTypes.string,
          alt: PropTypes.string,
        }),
      }),
    }),
  }).isRequired,
  location: PropTypes.shape({}).isRequired,
  pageContext: PropTypes.shape({ lang: PropTypes.string }).isRequired,
};

export default withPrismicPreview(EventTemplate, [
  { repositoryName: process.env.GATSBY_PRISMIC_REPO_NAME, linkResolver },
]);

export const query = graphql`
  query ($id: String!) {
    prismicEvent(id: { eq: $id }) {
      _previewable
      uid
      data {
        meta_title
        meta_description
        share_image {
          url
        }
        date(formatString: "MMMM DD, YYYY")
        title {
          text
        }
        description {
          html
        }
        image {
          alt
          localFile {
            childImageSharp {
              gatsbyImageData(width: 600)
            }
          }
        }
      }
    }
  }
`;
